/**
 * Звонок, живущий отдельно от экрана звонка (аудит удобства 2026-10-04, P0-5).
 *
 * <p><b>Зачем.</b> Раньше сессией владел экран `Call`: «Назад» размонтировал его, а вместе с ним
 * и `useVoiceCallSession` — то есть молча клал трубку. Свернуть разговор и заглянуть в переписку
 * было нельзя. Теперь сессия принадлежит приложению: её держит {@code ActiveCallHost} в корне, а
 * экран звонка только показывает её. «Назад» сворачивает, плашка «Вернуться к звонку» возвращает.
 *
 * <p><b>Почему модуль, а не контекст React.</b> Читают звонок те, кто стоит в разных ветках
 * дерева: экран звонка, плашка над навигатором, кнопки звонка в чатах и списке, обработчики
 * уведомлений. И у состояния две скорости: «какой звонок идёт» меняется раз за звонок, а
 * снимок сессии — на каждый кадр участников. Поэтому подписок две: хост слушает только первую,
 * иначе его же публикация снимка перерисовывала бы его самого по кругу.
 */

import type { RootStackParamList } from '../navigation/types';

export type CallSessionParams = RootStackParamList['Call'];

/**
 * Чем кончился звонок — для итоговой карточки и записи в историю.
 *
 * <ul>
 *   <li>`hangup` — положили трубку мы;</li>
 *   <li>`remote_hangup` — собеседник, уже после ответа;</li>
 *   <li>`busy` / `declined` / `no_answer` — исходящий не состоялся по вине другой стороны;</li>
 *   <li>`cancelled` — мы сами отменили исходящий до ответа;</li>
 *   <li>`missed` — входящий, на который не ответили (звонящий положил трубку или вышло время);</li>
 *   <li>`rejected` — входящий отклонили мы;</li>
 *   <li>`answered_elsewhere` — ответили с другого нашего устройства;</li>
 *   <li>`failed` — оборвалась связь или не поднялся групповой звонок.</li>
 * </ul>
 */
export type CallEndReason =
  | 'hangup'
  | 'remote_hangup'
  | 'busy'
  | 'declined'
  | 'no_answer'
  | 'cancelled'
  | 'missed'
  | 'rejected'
  | 'answered_elsewhere'
  | 'failed';

/** Что известно о звонке, пока он идёт и сразу после конца. */
export type CallSessionMeta = {
  /** Номер сессии: новый звонок — новый номер, даже в том же чате. */
  key: number;
  params: CallSessionParams;
  startedAt: number;
};

export type CallSessionEnded = {
  reason: CallEndReason;
  /** Длительность разговора в секундах (0, если не ответили). */
  durationSec: number;
  at: number;
};

/**
 * Снимок сессии, который публикует хост. `vc` — то, что вернул `useVoiceCallSession`; тип
 * объявлен обобщённо, чтобы этот модуль не тянул за собой WebRTC и LiveKit (его импортируют
 * экраны, которым нельзя грузить нативные звонковые модули при открытии).
 */
export type CallSessionLive<Vc = unknown, Extra = unknown> = {
  key: number;
  vc: Vc;
  extra: Extra;
  ended: CallSessionEnded | null;
};

type Listener = () => void;

let meta: CallSessionMeta | null = null;
let live: CallSessionLive | null = null;
let nextKey = 1;
const metaListeners = new Set<Listener>();
const liveListeners = new Set<Listener>();

function emit(set: Set<Listener>): void {
  // Копия: слушатель может отписаться прямо в обработчике.
  [...set].forEach((l) => l());
}

export function subscribeCallSessionMeta(listener: Listener): () => void {
  metaListeners.add(listener);
  return () => {
    metaListeners.delete(listener);
  };
}

export function subscribeCallSessionLive(listener: Listener): () => void {
  liveListeners.add(listener);
  return () => {
    liveListeners.delete(listener);
  };
}

export function getCallSessionMeta(): CallSessionMeta | null {
  return meta;
}

export function getCallSessionLive(): CallSessionLive | null {
  return live;
}

/**
 * Идёт ли разговор (или вызов) прямо сейчас — то есть сессия есть и ещё не закончилась.
 * Закончившаяся, но не снятая сессия — это итоговая карточка, она новому звонку не мешает.
 */
export function isCallSessionLive(): boolean {
  if (!meta) return false;
  return !(live && live.key === meta.key && live.ended);
}

/**
 * Начать сессию звонка.
 *
 * <p>Живая сессия не заменяется никогда: второй звонок поверх идущего — это два микрофона и
 * два набора гудков. Решать, что делать (вернуть к идущему, сказать «вы уже в звонке»), — дело
 * вызывающего; отсюда возвращается ключ той сессии, которая в итоге действует.
 */
export function beginCallSession(params: CallSessionParams): { key: number; started: boolean } {
  if (meta && isCallSessionLive()) {
    return { key: meta.key, started: false };
  }
  meta = { key: nextKey++, params, startedAt: Date.now() };
  live = null;
  emit(metaListeners);
  emit(liveListeners);
  return { key: meta.key, started: true };
}

/** Снять сессию. Ключ обязателен: таймер карточки прошлого звонка не должен снять новый. */
export function endCallSession(key: number): void {
  if (!meta || meta.key !== key) return;
  meta = null;
  live = null;
  emit(metaListeners);
  emit(liveListeners);
}

/** Хост публикует свежий снимок своей сессии. Снимок чужой (снятой) сессии отбрасывается. */
export function publishCallSessionLive(next: CallSessionLive): void {
  if (!meta || meta.key !== next.key) return;
  live = next;
  emit(liveListeners);
}

/**
 * Тот же звонок или другой. Сравнивается чат: в одном чате одновременно идёт не больше одного
 * разговора, а кнопка звонка в чате, где разговор уже идёт, должна вернуть к нему.
 */
export function isSameCallChat(a: Pick<CallSessionParams, 'chatId'>, b: Pick<CallSessionParams, 'chatId'>): boolean {
  const canon = (id: string) => id.trim().replace(/-/g, '').toLowerCase();
  return canon(a.chatId) === canon(b.chatId);
}

/*
 * Открыт ли сейчас экран звонка. От этого зависят две вещи: плашка «Вернуться к звонку» видна
 * только когда экрана нет, а конец свёрнутого звонка объясняется коротким уведомлением — карточку
 * показывать негде.
 */
let attachedKey: number | null = null;
let attachedCount = 0;
const attachListeners = new Set<Listener>();

export function subscribeCallScreenAttachment(listener: Listener): () => void {
  attachListeners.add(listener);
  return () => {
    attachListeners.delete(listener);
  };
}

/** Ключ сессии, которую сейчас показывает экран звонка, или `null`. */
export function getAttachedCallScreenKey(): number | null {
  return attachedCount > 0 ? attachedKey : null;
}

/**
 * Экран звонка показывает сессию `key`. Возвращает отписку для размонтирования. Счётчик, а не
 * флаг: при переходе со звонка на звонок новый экран монтируется раньше, чем снимается старый.
 */
export function attachCallScreen(key: number): () => void {
  attachedKey = key;
  attachedCount += 1;
  emit(attachListeners);
  let detached = false;
  return () => {
    if (detached) return;
    detached = true;
    attachedCount = Math.max(0, attachedCount - 1);
    emit(attachListeners);
  };
}

/** Только для тестов. */
export function resetCallSessionStoreForTests(): void {
  meta = null;
  live = null;
  nextKey = 1;
  attachedKey = null;
  attachedCount = 0;
  metaListeners.clear();
  liveListeners.clear();
  attachListeners.clear();
}
